import { Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnInit, Output, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../base.component';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { NHATestDirective } from '../../directives/test/test.directive';
import { MatInputModule } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';

@Component({
	selector: 'nha-search-box',
	templateUrl: './search-box.component.html',
	styleUrls: ['./search-box.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => NHASearchBoxComponent),
			multi: true
		}
	],
	imports: [NHATestDirective, CommonModule, MatInputModule, MatIconModule, FormsModule, ReactiveFormsModule, MatIconButton]
})
export class NHASearchBoxComponent extends BaseComponent implements OnInit, ControlValueAccessor {
	private _ngZone = inject(NgZone);

	@ViewChild('searchInput', { read: ElementRef })
	private _searchInput: ElementRef;

	isOpen: boolean = false;
	searchTerm: FormControl<string> = new FormControl<string>(null);
	showClearButton = false;

	@Input() lockOpen: boolean = false;
	@Input()
	set value(value: string) {
		if (this.searchTerm.value !== value) {
			this.showClearButton = value && value.length > 0;

			if (value && !this.isOpen) {
				this.open(false);
			}

			this.searchTerm.setValue(value, { emitEvent: false });
		}
	}
	@Input() debounceTime: number = 100;
	@Input() placeholder: string = '';
	@Input() boxShadow: boolean = true;
	@Input() gray: boolean = false;
	@Input() minWidth: string = '200px';

	@Output() readonly changed: EventEmitter<string> = new EventEmitter<string>();
	@Output() readonly opened: EventEmitter<void> = new EventEmitter<void>();
	@Output() readonly closed: EventEmitter<void> = new EventEmitter<void>();
	@Output() readonly focused: EventEmitter<void> = new EventEmitter<void>();
	@Output() readonly blurred: EventEmitter<void> = new EventEmitter<void>();

	ngOnInit(): void {
		if (this.lockOpen) {
			this.isOpen = true;
		}

		this.searchTerm.valueChanges.pipe(takeUntil(this.componentDestroyed$), debounceTime(this.debounceTime)).subscribe((term) => {
			this.showClearButton = !!(term && term.length > 0);
			this.changed.emit(term);
			this.onChangeCallback(term);
		});
	}

	toggleVisibility(): void {
		if (this.lockOpen) {
			return;
		}
		if (this.isOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	close(): void {
		this.isOpen = false;
		this.closed.emit();
	}

	open(focusInput = true): void {
		this.isOpen = true;
		this.opened.emit();

		if (focusInput) {
			this.focusInput();
		}
	}

	clear(): void {
		this.searchTerm.setValue('');
		this.changed.emit('');
		this.onChangeCallback('');
		this.focusInput();
	}

	focusInput(): void {
		if (this._ngZone.isStable) {
			this._searchInput.nativeElement.focus();
		} else {
			this._ngZone.onStable
				.asObservable()
				.pipe(take(1))
				.subscribe(() => this._searchInput.nativeElement.focus());
		}
	}

	onInputBlur(): void {
		this.blurred.emit();
		this.onTouchedCallback();
	}

	onInputFocus(): void {
		this.focused.emit();
	}

	// ControlValueAccessor interface methods
	private onChangeCallback: (_: any) => void = () => {};
	private onTouchedCallback: () => void = () => {};

	writeValue(value: any): void {
		this.searchTerm.setValue(value);
		this.showClearButton = value && value.length > 0;
	}

	registerOnChange(fn: any): void {
		this.onChangeCallback = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouchedCallback = fn;
	}
}
